import { ArrowOutward, Assignment } from "@mui/icons-material";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useFetch } from "../../../../../../hooks/Fetching";
import { CustomDay } from "../therapyView/TherapySchedule";
import dayjs from "dayjs";
import { SINGLE_THERAPY_VIEW } from "../../../../../../enums/UrlPaths";
import { GET_PET_RECORDS, GET_THERAPY_DAY_DATES } from "../../../../../../enums/ApiPaths";
import { PET_HEALTH_RECORDS, THERAPY_DAY_DATES } from "../../../../../../enums/QueryKeys";
import { getEarliestDate } from "../../../../../../utils/DateTimeRepository";

export default function HealthRecords() {
  const { id: petId } = useParams();
  const { data, isLoading, error } = useFetch(
    GET_PET_RECORDS + petId,
    null,
    PET_HEALTH_RECORDS + petId,
  );
  const { t } = useTranslation();

  if (isLoading) {
    return <LinearProgress />;
  } else if (error) {
    return <Typography color={"error"}>{t("ErrorLoadingData")}</Typography>;
  } else if (!data || data?.length == 0) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography color={"GrayText"}>
            {t("NoHealthRecordsForPet")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
            <Assignment color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700}>
              {t("CompleteHealthRecords")}
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} xl={3}>
              <CalendarComponent/>
            </Grid>
            <Grid item xs={12} xl={9}>
              <TableComponent data={data} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}

function TableComponent({ data = [] }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  function handleNavigate(id) {
    navigate(SINGLE_THERAPY_VIEW + id);
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 3,
        overflow: "auto",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.8,
        )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
      }}
    >
      <Table sx={{ minWidth: 450 }}>
        <TableHead>
          <TableRow
            sx={{
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.1,
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            }}
          >
            {[
              t("TherapyName"),
              t("StartDate"),
              t("EndDate"),
              t("IsDone"),
              t("Details"),
            ].map((header) => (
              <TableCell
                key={header}
                sx={{ fontWeight: 800, fontSize: "1rem", py: 3 }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((day, index) => (
            <TableRow
              key={index}
              sx={{
                "&:hover": {
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.05,
                  )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <TableCell>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    wordBreak: "break-all",
                  }}
                >
                  {day.therapyName}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={700} color="primary">
                  {day.startDate}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={700} color="primary">
                  {day.endDate || "N/A"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={700} color="primary">
                  {day.complete ? (
                    <Chip color="primary" label={t("Yes")} />
                  ) : (
                    <Chip color="error" label={t("No")} />
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={700} color="primary">
                  <Tooltip title={t("ViewTherapyDetails")}>
                    <IconButton onClick={() => handleNavigate(day.id)}>
                      <ArrowOutward />
                    </IconButton>
                  </Tooltip>
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CalendarComponent() {
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    data: responseData,
    isLoading,
    error,
  } = useFetch(
    GET_THERAPY_DAY_DATES + id,
    null,
    THERAPY_DAY_DATES + id,
  );

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          height: "100%",
        }}
      >
        <Skeleton height={"100%"} />
      </Paper>
    );
  } else if (error) {
    return <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          height: "100%",
        }}
      >
        <Typography color={"error"}>{t("ErrorFetchingTherapyDayDates")}</Typography>
      </Paper>
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Typography variant="h6" fontWeight="500" mb={3} color="text.primary">
        {t("TherapyListCalendar")}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <DateCalendar
          referenceDate={getEarliestDate(responseData?.map((d) => d.date))}
          sx={{
            "& .MuiPickersDay-root": {
              fontSize: "0.875rem",
            },
          }}
          slots={{
            day: (props) => {
              const match = responseData?.find((d) =>
                dayjs(d.date).isSame(props.day, "day"),
              );

              return (
                <CustomDay
                  {...props}
                  selected={match && !match?.green && !match?.red}
                  isGreen={match?.green}
                  isRed={match?.red}
                />
              );
            },
          }}
        />
      </Box>

      <TableLegend />
    </Paper>
  );
}

function TableLegend() {
  return (
    <Stack justifyContent={"center"} alignItems={"center"}>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          mt: 3,
          justifyContent: "center",
        }}
      >
        <Legend />
        <Legend color="primary" title="Scheduled" />
      </Box>
      <Legend color="error" title="HasUnfinishedTherapyToday" />
    </Stack>
  );
}

function Legend({ color = "success", title = "Completed" }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: `${color}.main`,
        }}
      />
      <Typography variant="caption">{t(title)}</Typography>
    </Box>
  );
}
